import React, { useEffect, useState } from 'react';
import {
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarIcon,
  UsersIcon,
  GlobeAltIcon,
  LockClosedIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import Helper from "../helpers";
import config from "../config";
                                
const baseUrl = config.apiBaseUrl;
                                
const ClassAdminView = () => {
    const [venueKeyOptions, setVenueKeyOptions] = useState([]);
    const [currentVenueKey, setCurrentVenueKey] = useState('');
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedClasses, setExpandedClasses] = useState(new Set());
    const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());

    // Edit modal state
    const [editModal, setEditModal] = useState(null);
    const [editForm, setEditForm] = useState({ name: '', description: '', startDate: '', price: '', cycleType: 1 });
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState('');
    const [removingId, setRemovingId] = useState(null);

    const cycleTypes = [
        { value: 0, label: 'Daily' },
        { value: 1, label: 'Weekly' },
        { value: 2, label: 'Monthly' },
        { value: 3, label: 'Quarterly' },
        { value: 4, label: 'Yearly' },
    ];

    useEffect(() => {
        Helper.getAdminVenues(setCurrentVenueKey, setVenueKeyOptions);
    }, []);

    useEffect(() => {
        if (!currentVenueKey) return;
        const fetchClasses = async () => {
            setLoading(true);
            try {
                const token = await Helper.getCookie('wh-jwt');
                const res = await fetch(`${baseUrl}/api/classes/admin/${currentVenueKey}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const classList = await res.json();
                setClasses(classList);
            } catch (error) {
                console.error('Error fetching classes:', error);
            }
            setLoading(false);
        };
        fetchClasses();
    }, [currentVenueKey]);

    const markAsPaid = async (classId, userId, classDate = null) => {
        const token = await Helper.getCookie('wh-jwt');
        const endpoint = classDate 
            ? `${baseUrl}/api/classes/markpaid/${currentVenueKey}/${classId}/${userId}?date=${encodeURIComponent(classDate)}`
            : `${baseUrl}/api/classes/markpaid/${currentVenueKey}/${classId}/${userId}`;
            
        const res = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
            // Refresh data
            const newRes = await fetch(`${baseUrl}/api/classes/admin/${currentVenueKey}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const classList = await newRes.json();
            setClasses(classList);
        }
    };

    const markAsUnpaid = async (classId, userId, classDate = null) => {
        const token = await Helper.getCookie('wh-jwt');
        const endpoint = classDate 
            ? `${baseUrl}/api/classes/markunpaid/${currentVenueKey}/${classId}/${userId}?date=${encodeURIComponent(classDate)}`
            : `${baseUrl}/api/classes/markunpaid/${currentVenueKey}/${classId}/${userId}`;
            
        const res = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
        });

        if (res.ok) {
            // Refresh data
            const newRes = await fetch(`${baseUrl}/api/classes/admin/${currentVenueKey}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const classList = await newRes.json();
            setClasses(classList);
        }
    };

    const updateClassToPublic = async (classId) => {
        const token = await Helper.getCookie('wh-jwt');
        const res = await fetch(`${baseUrl}/api/classes/admin/update-to-public`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                ClassId: classId,
                VenueKey: currentVenueKey
            })
        });

        if (res.ok) {
            // Refresh data
            const newRes = await fetch(`${baseUrl}/api/classes/admin/${currentVenueKey}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const classList = await newRes.json();
            setClasses(classList);
        }
    };

    const updateClassToPrivate = async (classId) => {
        const token = await Helper.getCookie('wh-jwt');
        const res = await fetch(`${baseUrl}/api/classes/admin/update-to-private`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                ClassId: classId,
                VenueKey: currentVenueKey
            })
        });

        if (res.ok) {
            // Refresh data
            const newRes = await fetch(`${baseUrl}/api/classes/admin/${currentVenueKey}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const classList = await newRes.json();
            setClasses(classList);
        }
    };

    const refreshClasses = async () => {
        const token = await Helper.getCookie('wh-jwt');
        const res = await fetch(`${baseUrl}/api/classes/admin/${currentVenueKey}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        const classList = await res.json();
        setClasses(classList);
    };

    const openEditModal = (venueClass) => {
        setEditForm({
            name: venueClass.name,
            description: venueClass.description,
            // Pre-fill with the class's current start date/time, formatted for a
            // datetime-local input. dayjs formats in local time with no timezone
            // conversion, matching how it's sent back on save (see saveEditedClass).
            startDate: dayjs(venueClass.startDate).format('YYYY-MM-DDTHH:mm'),
            price: venueClass.price?.toString() ?? '',
            cycleType: venueClass.cycleType ?? 1,
        });
        setEditError('');
        setEditModal(venueClass);
    };

    const saveEditedClass = async () => {
        if (!editForm.name.trim()) {
            setEditError('Please enter a class name.');
            return;
        }
        if (!editForm.startDate) {
            setEditError('Please set a start date and time.');
            return;
        }
        setEditLoading(true);
        setEditError('');
        try {
            const token = await Helper.getCookie('wh-jwt');
            const res = await fetch(`${baseUrl}/api/classes/admin/update`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    VenueKey: currentVenueKey,
                    ClassId: editModal.id,
                    Name: editForm.name,
                    Description: editForm.description,
                    // No trailing Z: this is a local wall-clock time, not UTC (see
                    // BulkClass.js for the full explanation of the display-time drift
                    // appending a false Z here used to cause).
                    StartDate: dayjs(editForm.startDate).format('YYYY-MM-DDTHH:mm:ss'),
                    Price: parseFloat(editForm.price),
                    CycleType: parseInt(editForm.cycleType),
                })
            });
            if (!res.ok) {
                setEditError('Failed to save changes. Please try again.');
                setEditLoading(false);
                return;
            }

            setEditModal(null);
            await refreshClasses();
        } catch (e) {
            setEditError('An unexpected error occurred.');
        }
        setEditLoading(false);
    };

    const removeClass = async (venueClass) => {
        if (!window.confirm(`Remove "${venueClass.name}"? It'll stop showing here and to members, but its booking history is kept.`)) {
            return;
        }
        setRemovingId(venueClass.id);
        try {
            const token = await Helper.getCookie('wh-jwt');
            const res = await fetch(`${baseUrl}/api/classes/admin/retire`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ ClassId: venueClass.id, VenueKey: currentVenueKey })
            });
            if (res.ok) {
                await refreshClasses();
            }
        } finally {
            setRemovingId(null);
        }
    };

    const toggleDescription = (classId) => {
        setExpandedDescriptions(prev => {
            const next = new Set(prev);
            if (next.has(classId)) {
                next.delete(classId);
            } else {
                next.add(classId);
            }
            return next;
        });
    };

    // Helper function to get upcoming classes for a venue class
    const getUpcomingClasses = (venueClass) => {
        if (!venueClass.userClasses || venueClass.userClasses.length === 0) {
            return [];
        }

        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        // Group users by their payment/class date
        const usersByDate = {};
        
        venueClass.userClasses.forEach(userClass => {
            if (userClass.paymentDate) {
                const classDate = new Date(userClass.paymentDate);
                const dateString = classDate.toISOString().split('T')[0];
                
                if (!usersByDate[dateString]) {
                    usersByDate[dateString] = {
                        date: classDate,
                        users: []
                    };
                }
                usersByDate[dateString].users.push(userClass);
            }
        });
        
        // Get upcoming dates (including today) and sort them
        return Object.values(usersByDate)
            .filter(dateGroup => dateGroup.date >= currentDate)
            .sort((a, b) => a.date - b.date);
    };

    // Helper function to check if class has any future bookings
    const hasFutureBookings = (venueClass) => {
        return getUpcomingClasses(venueClass).length > 0;
    };

    const getCycleTypeName = (cycleType) => {
        const types = { 0: 'Daily', 1: 'Weekly', 2: 'Monthly', 3: 'Quarterly', 4: 'Yearly' };
        return types[cycleType] || 'Weekly';
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="card p-6 mb-6">
                <h1 className="text-3xl font-bold text-white mb-4">Class Admin Management</h1>
                
                {/* Venue Selector */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                        Select Venue
                    </label>
                    <select
                        value={currentVenueKey}
                        onChange={e => setCurrentVenueKey(e.target.value)}
                        className="input-field max-w-xs"
                    >
                        <option value="">Select a venue...</option>
                        {venueKeyOptions.map(vk => (
                            <option key={vk} value={vk}>{vk}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Classes List */}
            {/* Removed (retired) classes are left out entirely rather than shown greyed-out -
                their booking history is kept server-side, they just don't clutter this list. */}
            <div className="space-y-6">
                {classes.filter(venueClass => !(venueClass.endDate && new Date(venueClass.endDate) <= new Date())).map(venueClass => {
                    const upcomingClasses = getUpcomingClasses(venueClass);
                    const totalEnrollments = venueClass.userClasses ? venueClass.userClasses.length : 0;
                    const hasUpcoming = hasFutureBookings(venueClass);
                    const isExpanded = expandedClasses.has(venueClass.id);
                    const isDescriptionExpanded = expandedDescriptions.has(venueClass.id);
                    const isLongDescription = (venueClass.description || '').length > 140;
                    const isRemoving = removingId === venueClass.id;

                    return (
                        <div key={venueClass.id} className="card overflow-hidden">
                            {/* Class Header */}
                            <div className="px-6 py-4 border-b border-gray-600 bg-gray-700">
                                <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                                    <div className="flex-1 min-w-0">
                                        <h2 className="text-2xl font-bold text-white mb-2">
                                            {venueClass.name}
                                        </h2>
                                        <p className="text-gray-300 text-sm mb-2">
                                            {isLongDescription && !isDescriptionExpanded
                                                ? `${venueClass.description.slice(0, 140)}…`
                                                : venueClass.description}
                                            {isLongDescription && (
                                                <button
                                                    onClick={() => toggleDescription(venueClass.id)}
                                                    className="ml-2 inline-flex items-center text-blue-400 hover:text-blue-300 text-sm font-medium"
                                                >
                                                    {isDescriptionExpanded ? (
                                                        <>See less <ChevronUpIcon className="h-4 w-4 ml-0.5" /></>
                                                    ) : (
                                                        <>See more <ChevronDownIcon className="h-4 w-4 ml-0.5" /></>
                                                    )}
                                                </button>
                                            )}
                                        </p>
                                        <div className="flex flex-wrap items-center text-gray-200 text-base gap-x-5 gap-y-2 mt-3">
                                            <div className="flex items-center font-medium">
                                                <CalendarIcon className="h-5 w-5 mr-1.5 text-gray-400" />
                                                {dayjs(venueClass.startDate).format('ddd D MMM YYYY, HH:mm')}
                                            </div>
                                            <div className="flex items-center">
                                                <span className="text-green-400 font-semibold text-lg">
                                                    £{venueClass.price?.toFixed(2)}
                                                </span>
                                            </div>
                                            {venueClass.cycleType !== undefined && (
                                                <span className="text-emerald-400 font-medium">
                                                    {getCycleTypeName(venueClass.cycleType)} Recurring
                                                </span>
                                            )}
                                            <div className="flex items-center">
                                                {venueClass.isPublic ? (
                                                    <>
                                                        <GlobeAltIcon className="h-4 w-4 mr-1 text-blue-400" />
                                                        <span className="text-blue-400 font-medium">Public</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <LockClosedIcon className="h-4 w-4 mr-1 text-orange-400" />
                                                        <span className="text-orange-400 font-medium">Private</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-3 md:mt-0 md:ml-4 flex items-center space-x-2 flex-shrink-0">
                                        {venueClass.isPublic ? (
                                            <button
                                                onClick={() => updateClassToPrivate(venueClass.id)}
                                                className="bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium px-3 py-2 rounded-lg transition-colors duration-200 flex items-center"
                                                title="Make this class private"
                                            >
                                                <LockClosedIcon className="h-4 w-4 mr-1" />
                                                Make Private
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => updateClassToPublic(venueClass.id)}
                                                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-3 py-2 rounded-lg transition-colors duration-200 flex items-center"
                                                title="Make this class public"
                                            >
                                                <GlobeAltIcon className="h-4 w-4 mr-1" />
                                                Make Public
                                            </button>
                                        )}
                                        <button
                                            onClick={() => openEditModal(venueClass)}
                                            className="bg-gray-600 hover:bg-gray-500 text-white text-sm font-medium px-3 py-2 rounded-lg transition-colors duration-200 flex items-center"
                                            title="Edit this class's details"
                                        >
                                            <PencilIcon className="h-4 w-4 mr-1" />
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => removeClass(venueClass)}
                                            disabled={isRemoving}
                                            className="bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-medium px-3 py-2 rounded-lg transition-colors duration-200 flex items-center"
                                            title="Remove this class"
                                        >
                                            <TrashIcon className="h-4 w-4 mr-1" />
                                            {isRemoving ? 'Removing…' : 'Remove'}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Class Content */}
                            {hasUpcoming ? (
                                <div className="p-6">
                                    {!isExpanded ? (
                                        /* Summary View - Next upcoming class only */
                                        <div>
                                            <h3 className="text-lg font-semibold text-white mb-4">
                                                Next Class
                                            </h3>
                                            {upcomingClasses.slice(0, 1).map((dateGroup, index) => {
                                                const paidCount = dateGroup.users.filter(user => user.hasPaid).length;
                                                const unpaidCount = dateGroup.users.filter(user => !user.hasPaid).length;
                                                const isToday = dateGroup.date.toDateString() === new Date().toDateString();
                                                
                                                return (
                                                    <div key={index} className="bg-gray-800 rounded-lg p-4">
                                                        <div className="flex items-center justify-between mb-4">
                                                            <div>
                                                                <h4 className="text-white font-medium text-lg">
                                                                    {dateGroup.date.toLocaleDateString('en-GB', { 
                                                                        weekday: 'long', 
                                                                        day: 'numeric', 
                                                                        month: 'long',
                                                                        year: 'numeric'
                                                                    })}
                                                                </h4>
                                                                <div className="flex items-center space-x-4 text-sm mt-1">
                                                                    <span className="text-green-400 font-medium">
                                                                        {paidCount} paid
                                                                    </span>
                                                                    <span className="text-red-400 font-medium">
                                                                        {unpaidCount} unpaid
                                                                    </span>
                                                                    <span className="text-gray-400">
                                                                        {dateGroup.users.length} attending
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            {isToday && (
                                                                <span className="bg-blue-600 text-white text-xs px-3 py-1 rounded-full font-medium">
                                                                    TODAY
                                                                </span>
                                                            )}
                                                        </div>
                                                        
                                                        {/* Student List */}
                                                        <div className="space-y-2">
                                                            {dateGroup.users.map((userClass, userIndex) => (
                                                                <div 
                                                                    key={userIndex}
                                                                    className={`flex items-center justify-between p-3 rounded-lg ${
                                                                        userClass.hasPaid 
                                                                            ? 'bg-green-900/30 border border-green-700/50' 
                                                                            : 'bg-red-900/30 border border-red-700/50'
                                                                    }`}
                                                                >
                                                                    <div className="flex items-center">
                                                                        <div className="h-10 w-10 rounded-full bg-gray-600 flex items-center justify-center mr-3">
                                                                            <UserIcon className="h-5 w-5 text-gray-300" />
                                                                        </div>
                                                                        <div>
                                                                            <div className="text-white font-medium">
                                                                                {userClass.userName}
                                                                            </div>
                                                                            <div className="flex items-center text-sm">
                                                                                {userClass.hasPaid ? (
                                                                                    <>
                                                                                        <CheckCircleIcon className="h-4 w-4 text-green-400 mr-1" />
                                                                                        <span className="text-green-400">Paid</span>
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <XCircleIcon className="h-4 w-4 text-red-400 mr-1" />
                                                                                        <span className="text-red-400">Payment Pending</span>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    
                                                                    <div>
                                                                        {userClass.hasPaid ? (
                                                                            <button
                                                                                onClick={() => markAsUnpaid(venueClass.id, userClass.userId, userClass.paymentDate)}
                                                                                className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-2 px-3 rounded-lg transition-colors duration-200 flex items-center"
                                                                            >
                                                                                <XCircleIcon className="h-4 w-4 mr-1" />
                                                                                Mark Unpaid
                                                                            </button>
                                                                        ) : (
                                                                            <button
                                                                                onClick={() => markAsPaid(venueClass.id, userClass.userId, userClass.paymentDate)}
                                                                                className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 px-3 rounded-lg transition-colors duration-200 flex items-center"
                                                                            >
                                                                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                                                                                Mark Paid
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        
                                                        {upcomingClasses.length > 1 && (
                                                            <div className="mt-4 text-center">
                                                                <button
                                                                    onClick={() => setExpandedClasses(prev => new Set([...prev, venueClass.id]))}
                                                                    className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                                                                >
                                                                    Show {upcomingClasses.length - 1} more upcoming classes
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        /* Detailed View - All upcoming classes */
                                        <div>
                                            <h3 className="text-lg font-semibold text-white mb-4">
                                                All Upcoming Classes
                                            </h3>
                                            <div className="space-y-4">
                                                {upcomingClasses.map((dateGroup, index) => {
                                                    const paidCount = dateGroup.users.filter(user => user.hasPaid).length;
                                                    const unpaidCount = dateGroup.users.filter(user => !user.hasPaid).length;
                                                    const isToday = dateGroup.date.toDateString() === new Date().toDateString();
                                                    
                                                    return (
                                                        <div key={index} className="border border-gray-600 rounded-lg overflow-hidden">
                                                            <div className="bg-gray-700 px-4 py-3 border-b border-gray-600">
                                                                <div className="flex items-center justify-between">
                                                                    <h4 className="text-lg font-medium text-white">
                                                                        {dateGroup.date.toLocaleDateString('en-GB', { 
                                                                            weekday: 'long', 
                                                                            day: 'numeric', 
                                                                            month: 'long',
                                                                            year: 'numeric'
                                                                        })}
                                                                    </h4>
                                                                    <div className="flex items-center space-x-3">
                                                                        {isToday && (
                                                                            <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded">
                                                                                TODAY
                                                                            </span>
                                                                        )}
                                                                        <div className="flex items-center space-x-4 text-sm">
                                                                            <span className="text-green-400">
                                                                                {paidCount} paid
                                                                            </span>
                                                                            <span className="text-red-400">
                                                                                {unpaidCount} unpaid
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="p-4">
                                                                <div className="space-y-2">
                                                                    {dateGroup.users.map((userClass, userIndex) => (
                                                                        <div 
                                                                            key={userIndex}
                                                                            className={`flex items-center justify-between p-3 rounded-lg ${
                                                                                userClass.hasPaid 
                                                                                    ? 'bg-green-900/20' 
                                                                                    : 'bg-red-900/20'
                                                                            }`}
                                                                        >
                                                                            <div className="flex items-center">
                                                                                <div className="h-8 w-8 rounded-full bg-gray-600 flex items-center justify-center mr-3">
                                                                                    <UserIcon className="h-4 w-4 text-gray-300" />
                                                                                </div>
                                                                                <div>
                                                                                    <div className="text-white font-medium text-sm">
                                                                                        {userClass.userName}
                                                                                    </div>
                                                                                    <div className="flex items-center text-xs">
                                                                                        {userClass.hasPaid ? (
                                                                                            <>
                                                                                                <CheckCircleIcon className="h-3 w-3 text-green-400 mr-1" />
                                                                                                <span className="text-green-400">Paid</span>
                                                                                            </>
                                                                                        ) : (
                                                                                            <>
                                                                                                <XCircleIcon className="h-3 w-3 text-red-400 mr-1" />
                                                                                                <span className="text-red-400">Pending</span>
                                                                                            </>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                            
                                                                            <div>
                                                                                {userClass.hasPaid ? (
                                                                                    <button
                                                                                        onClick={() => markAsUnpaid(venueClass.id, userClass.userId, userClass.paymentDate)}
                                                                                        className="bg-red-600 hover:bg-red-700 text-white text-xs font-medium py-1 px-2 rounded transition-colors duration-200 flex items-center"
                                                                                    >
                                                                                        <XCircleIcon className="h-3 w-3 mr-1" />
                                                                                        Unpaid
                                                                                    </button>
                                                                                ) : (
                                                                                    <button
                                                                                        onClick={() => markAsPaid(venueClass.id, userClass.userId, userClass.paymentDate)}
                                                                                        className="bg-green-600 hover:bg-green-700 text-white text-xs font-medium py-1 px-2 rounded transition-colors duration-200 flex items-center"
                                                                                    >
                                                                                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                                                                                        Paid
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                /* No upcoming classes - Compact display */
                                <div className="px-6 py-3 bg-gray-800/50">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <UsersIcon className="h-5 w-5 text-gray-500 mr-2" />
                                            <span className="text-sm text-gray-400">
                                                {totalEnrollments > 0 
                                                    ? `${totalEnrollments} past enrollments - no upcoming classes` 
                                                    : 'No students enrolled yet'
                                                }
                                            </span>
                                        </div>
                                        <span className="text-xs text-gray-500">Ready for bookings</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {classes.length === 0 && currentVenueKey && (
                <div className="card p-8 text-center">
                    <CalendarIcon className="h-12 w-12 text-gray-500 mx-auto mb-3" />
                    <h3 className="text-lg font-medium text-gray-400 mb-1">No Classes Found</h3>
                    <p className="text-gray-500">No classes are available for the selected venue.</p>
                </div>
            )}

            {/* Edit Class Modal */}
            {editModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                    <div className="bg-gray-800 border border-gray-600 rounded-xl w-full max-w-lg shadow-2xl">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-600">
                            <div>
                                <h2 className="text-lg font-bold text-white">Edit Class</h2>
                                <p className="text-sm text-gray-400 mt-0.5">
                                    Updates <span className="text-amber-400 font-medium">{editModal.name}</span> in place.
                                </p>
                            </div>
                            <button onClick={() => setEditModal(null)} className="text-gray-400 hover:text-white">
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        <div className="px-6 py-5 space-y-4">
                            {editError && (
                                <div className="bg-red-900/30 border border-red-600 text-red-300 rounded-lg px-4 py-2 text-sm">
                                    {editError}
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">Class Name</label>
                                <input type="text" value={editForm.name}
                                    onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                                    className="input-field w-full" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                                <textarea value={editForm.description}
                                    onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                                    rows={2} className="input-field w-full resize-none" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Start Date &amp; Time <span className="text-red-400">*</span></label>
                                    <input type="datetime-local" value={editForm.startDate}
                                        onChange={e => setEditForm(p => ({ ...p, startDate: e.target.value }))}
                                        className="input-field w-full" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Price (£)</label>
                                    <input type="number" step="0.01" min="0" value={editForm.price}
                                        onChange={e => setEditForm(p => ({ ...p, price: e.target.value }))}
                                        className="input-field w-full" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">Recurring Schedule</label>
                                <select value={editForm.cycleType}
                                    onChange={e => setEditForm(p => ({ ...p, cycleType: e.target.value }))}
                                    className="input-field w-full">
                                    {cycleTypes.map(ct => (
                                        <option key={ct.value} value={ct.value}>{ct.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-600">
                            <button onClick={() => setEditModal(null)} disabled={editLoading}
                                className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors">
                                Cancel
                            </button>
                            <button onClick={saveEditedClass} disabled={editLoading}
                                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-500 rounded-lg transition-colors flex items-center disabled:opacity-50">
                                {editLoading ? (
                                    <span className="flex items-center"><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>Saving…</span>
                                ) : (
                                    <span className="flex items-center"><PencilIcon className="h-4 w-4 mr-1" />Save Changes</span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClassAdminView;
